import { BarChart3, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  CandlestickSeries,
  ColorType,
  HistogramSeries,
  LineSeries,
  createChart,
  type CandlestickData,
  type HistogramData,
  type LineData,
  type UTCTimestamp,
} from "lightweight-charts";

import { formatPrice } from "../format";
import {
  analyticsDisclosure,
  analyticsReasonLabel,
  analyticsStatusLabel,
  analyticsTone,
  optionIntervalBasisLabel,
} from "../optionAnalytics";
import type {
  OptionContract,
  OptionGreekPoint,
  OptionInterval,
  PriceBar,
} from "../types";

type OptionMetric =
  | "price"
  | "implied_volatility"
  | "delta"
  | "gamma"
  | "theta"
  | "vega";

const METRICS: { value: OptionMetric; label: string; color: string }[] = [
  { value: "price", label: "Price", color: "#1976d2" },
  { value: "implied_volatility", label: "IV", color: "#7a4eb2" },
  { value: "delta", label: "Delta", color: "#d46b08" },
  { value: "gamma", label: "Gamma", color: "#00838f" },
  { value: "theta", label: "Theta", color: "#c62828" },
  { value: "vega", label: "Vega", color: "#2f7d43" },
];

interface OptionMiniChartProps {
  contract: OptionContract | null;
  interval: OptionInterval;
  bars: PriceBar[];
  greeks: OptionGreekPoint[];
  nativeInterval: boolean | null;
  loading: boolean;
  onIntervalChange: (interval: OptionInterval) => void;
  onClose: () => void;
}

interface OptionAnalyticsBadgeProps {
  contract: OptionContract;
  point?: OptionGreekPoint;
}

export function OptionAnalyticsBadge({
  contract,
  point,
}: OptionAnalyticsBadgeProps) {
  const calculationStatus =
    point?.calculation_status ?? contract.analytics_status;
  const calculationReason =
    point?.calculation_reason ?? contract.analytics_reason;
  const modelVersion = point?.model_version ?? contract.analytics_model;
  const calculationObservedAt =
    point?.observed_at ?? contract.analytics_observed_at;
  const calculationTone = analyticsTone(calculationStatus);
  const hasLocalModel = Boolean(
    contract.analytics_model || point?.basis.startsWith("calculated_"),
  );
  const analyticsBadge =
    calculationTone === "healthy" && hasLocalModel
      ? "LOCAL AMERICAN"
      : calculationTone === "healthy"
        ? "LEGACY ANALYTICS"
        : calculationStatus
          ? analyticsStatusLabel(calculationStatus)
          : point
            ? "LEGACY ANALYTICS"
            : "ANALYTICS PENDING";
  const observationState =
    point?.complete === false
      ? "TODAY LIVE"
      : point?.complete === true && point.basis === "calculated_close"
        ? "CLOSE FINAL"
        : null;

  return (
    <span
      className={`option-analytics-badge ${calculationTone}`}
      title={analyticsDisclosure({
        status: calculationStatus,
        reason: calculationReason,
        model: modelVersion,
        observedAt: calculationObservedAt,
      })}
    >
      {observationState && `${observationState} · `}
      {analyticsBadge}
    </span>
  );
}

function chartTime(value: string): UTCTimestamp {
  return Math.floor(new Date(value).getTime() / 1000) as UTCTimestamp;
}

export function OptionMiniChart({
  contract,
  interval,
  bars,
  greeks,
  nativeInterval,
  loading,
  onIntervalChange,
  onClose,
}: OptionMiniChartProps) {
  const container = useRef<HTMLDivElement>(null);
  const [metric, setMetric] = useState<OptionMetric>("price");
  const contractCode = contract?.code ?? null;
  const activeMetric = interval === "daily" ? metric : "price";
  const metricConfig = useMemo(
    () => METRICS.find((item) => item.value === activeMetric) ?? METRICS[0],
    [activeMetric],
  );
  const metricPoints = useMemo(
    () =>
      activeMetric === "price"
        ? []
        : greeks.filter((point) => point[activeMetric] !== null),
    [activeMetric, greeks],
  );
  const latestGreek = greeks.at(-1);
  const calculationReason =
    latestGreek?.calculation_reason ?? contract?.analytics_reason;
  const intervalBasis = optionIntervalBasisLabel(nativeInterval);

  useEffect(() => {
    if (!contractCode || !container.current) return;
    const hasData =
      activeMetric === "price" ? bars.length > 0 : metricPoints.length > 0;
    if (!hasData) return;
    const chart = createChart(container.current, {
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: "#ffffff" },
        textColor: "#53616a",
        fontSize: 10,
      },
      grid: {
        vertLines: { color: "#edf0f2" },
        horzLines: { color: "#e6eaed" },
      },
      rightPriceScale: {
        borderColor: "#d2d8dc",
        scaleMargins: { top: 0.08, bottom: 0.27 },
      },
      timeScale: {
        borderColor: "#d2d8dc",
        timeVisible: interval !== "daily",
        secondsVisible: false,
      },
    });
    if (activeMetric === "price") {
      const candles = chart.addSeries(CandlestickSeries, {
        upColor: "#e73d43",
        downColor: "#2f9e55",
        borderUpColor: "#e73d43",
        borderDownColor: "#2f9e55",
        wickUpColor: "#e73d43",
        wickDownColor: "#2f9e55",
      });
      const candleData: CandlestickData<UTCTimestamp>[] = bars
        .filter(
          (bar) =>
            bar.open !== null &&
            bar.high !== null &&
            bar.low !== null &&
            bar.close !== null,
        )
        .map((bar) => ({
          time: chartTime(bar.open_time),
          open: bar.open as number,
          high: bar.high as number,
          low: bar.low as number,
          close: bar.close as number,
        }));
      candles.setData(candleData);
      const volume = chart.addSeries(HistogramSeries, {
        priceScaleId: "volume",
        priceFormat: { type: "volume" },
        lastValueVisible: false,
        priceLineVisible: false,
      });
      chart.priceScale("volume").applyOptions({
        scaleMargins: { top: 0.82, bottom: 0 },
        borderVisible: false,
      });
      const volumeData: HistogramData<UTCTimestamp>[] = bars
        .filter((bar) => bar.volume !== null)
        .map((bar, index) => ({
          time: chartTime(bar.open_time),
          value: bar.volume ?? 0,
          color:
            index === 0 ||
            (bar.close ?? 0) >= (bars[index - 1]?.close ?? bar.close ?? 0)
              ? "rgba(231, 61, 67, 0.65)"
              : "rgba(47, 158, 85, 0.65)",
        }));
      volume.setData(volumeData);
    } else {
      chart.priceScale("right").applyOptions({
        scaleMargins: { top: 0.1, bottom: 0.1 },
      });
      const series = chart.addSeries(LineSeries, {
        color: metricConfig.color,
        lineWidth: 2,
        priceLineVisible: true,
        lastValueVisible: true,
      });
      const lineData: LineData<UTCTimestamp>[] = metricPoints.map((point) => ({
        time: chartTime(point.open_time),
        value: point[activeMetric] as number,
      }));
      series.setData(lineData);
    }
    chart.timeScale().fitContent();
    const observer = new ResizeObserver(() => chart.applyOptions({ autoSize: true }));
    observer.observe(container.current);
    return () => {
      observer.disconnect();
      chart.remove();
    };
  }, [
    activeMetric,
    bars,
    contractCode,
    interval,
    metricConfig.color,
    metricPoints,
  ]);

  return (
    <aside
      className={`option-detail ${contract ? "has-contract" : "empty"}`}
      aria-label="Option price chart"
    >
      <div className="option-detail-header">
        {contract ? (
          <>
            <div>
              <span className={`option-type ${contract.type}`}>{contract.type}</span>
              <strong>{contract.strike.toFixed(2)}</strong>
              <span>{contract.expiration}</span>
            </div>
            <button
              className="icon-button"
              type="button"
              onClick={onClose}
              aria-label="Close option chart"
              title="Close option chart"
            >
              <X size={16} />
            </button>
          </>
        ) : (
          <div className="option-detail-placeholder-title">
            <BarChart3 size={15} aria-hidden="true" />
            <strong>OPTION CHART</strong>
          </div>
        )}
      </div>
      <div className="option-detail-quote">
        <strong>{contract ? formatPrice(contract.mark ?? contract.last) : "—"}</strong>
        <span>BID {contract ? formatPrice(contract.bid) : "—"}</span>
        <span>ASK {contract ? formatPrice(contract.ask) : "—"}</span>
      </div>
      <div className="option-intervals" role="tablist" aria-label="Option interval">
        {(
          [
            { value: "1m", label: "1m" },
            { value: "5m", label: "5m" },
            { value: "30m", label: "30m" },
            { value: "daily", label: "Daily" },
          ] as const
        ).map(({ value, label }) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={interval === value}
            className={interval === value ? "active" : ""}
            disabled={!contract}
            onClick={() => onIntervalChange(value)}
          >
            {label}
          </button>
        ))}
        {contract && intervalBasis && (
          <span
            className={`option-interval-basis ${intervalBasis.toLowerCase()}`}
            title={
              nativeInterval
                ? "Provided in this interval by Databento"
                : "Aggregated locally from native one-minute Databento bars"
            }
          >
            {intervalBasis}
          </span>
        )}
      </div>
      <div
        className={`option-metrics ${interval !== "daily" ? "disabled" : ""}`}
        role="tablist"
        aria-label="Option chart metric"
      >
        {METRICS.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={activeMetric === value}
            className={activeMetric === value ? "active" : ""}
            disabled={!contract || (interval !== "daily" && value !== "price")}
            onClick={() => setMetric(value)}
          >
            {label}
          </button>
        ))}
        {contract && activeMetric !== "price" && (
          <OptionAnalyticsBadge contract={contract} point={latestGreek} />
        )}
      </div>
      <div className="option-chart-shell">
        <div ref={container} className="option-chart-canvas" />
        {!contract && (
          <div className="option-chart-empty">
            <BarChart3 size={22} aria-hidden="true" />
            <strong>NO CONTRACT SELECTED</strong>
            <span>Select a call or put price</span>
          </div>
        )}
        {contract && loading && (
          <div className="chart-state compact">Loading contract bars…</div>
        )}
        {contract &&
          !loading &&
          (activeMetric === "price"
            ? bars.length === 0
            : metricPoints.length === 0) && (
            <div className="chart-state compact">
              {activeMetric === "price"
                ? "No trades in this interval"
                : calculationReason
                  ? analyticsReasonLabel(calculationReason)
                  : `No ${metricConfig.label} daily history`}
            </div>
        )}
      </div>
    </aside>
  );
}
